import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const hasValidConfig = supabaseUrl && supabaseAnonKey &&
  supabaseUrl !== 'your_supabase_project_url' &&
  supabaseAnonKey !== 'your_supabase_anon_key';

// Singleton: reuse existing client across HMR reloads
const globalKey = '__supabase_client__' as const;
const globalWindow = globalThis as unknown as Record<string, SupabaseClient>;

let supabase: SupabaseClient;

if (globalWindow[globalKey]) {
  supabase = globalWindow[globalKey];
} else if (!hasValidConfig) {
  console.warn('⚠️ Supabase environment variables not configured. Database features will not work.');
  supabase = createClient('https://placeholder.supabase.co', 'placeholder-key');
  globalWindow[globalKey] = supabase;
} else {
  console.log('✅ Supabase client initialized');
  console.log('Supabase URL:', supabaseUrl);

  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });
  globalWindow[globalKey] = supabase;
}

export { supabase };

// Database types
export interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          email: string;
          name: string | null;
          role_id: string | null;
          company_id: string | null;
          department: string | null;
          phone: string | null;
          avatar_url: string | null;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id: string;
          email: string;
          name?: string | null;
          role_id?: string | null;
          company_id?: string | null;
          department?: string | null;
          phone?: string | null;
          avatar_url?: string | null;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          email?: string;
          name?: string | null;
          role_id?: string | null;
          company_id?: string | null;
          department?: string | null;
          phone?: string | null;
          avatar_url?: string | null;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      companies: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          website: string | null;
          address: any | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          website?: string | null;
          address?: any | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          website?: string | null;
          address?: any | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      products: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          part_number: string;
          category_id: string | null;
          type: 'pcb' | 'module' | 'system' | 'component';
          status: 'concept' | 'design' | 'prototype' | 'production' | 'eol';
          complexity: 'low' | 'medium' | 'high';
          current_revision: string;
          owner_id: string | null;
          created_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          part_number: string;
          category_id?: string | null;
          type?: 'pcb' | 'module' | 'system' | 'component';
          status?: 'concept' | 'design' | 'prototype' | 'production' | 'eol';
          complexity?: 'low' | 'medium' | 'high';
          current_revision?: string;
          owner_id?: string | null;
          created_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          part_number?: string;
          category_id?: string | null;
          type?: 'pcb' | 'module' | 'system' | 'component';
          status?: 'concept' | 'design' | 'prototype' | 'production' | 'eol';
          complexity?: 'low' | 'medium' | 'high';
          current_revision?: string;
          owner_id?: string | null;
          created_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      components: {
        Row: {
          id: string;
          part_number: string;
          description: string;
          category_id: string | null;
          component_type: 'ic' | 'passive' | 'connector' | 'mechanical' | 'pcb';
          manufacturer_id: string | null;
          manufacturer_part_number: string | null;
          package_type: string | null;
          specifications: any;
          datasheet_url: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          part_number: string;
          description: string;
          category_id?: string | null;
          component_type?: 'ic' | 'passive' | 'connector' | 'mechanical' | 'pcb';
          manufacturer_id?: string | null;
          manufacturer_part_number?: string | null;
          package_type?: string | null;
          specifications?: any;
          datasheet_url?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          part_number?: string;
          description?: string;
          category_id?: string | null;
          component_type?: 'ic' | 'passive' | 'connector' | 'mechanical' | 'pcb';
          manufacturer_id?: string | null;
          manufacturer_part_number?: string | null;
          package_type?: string | null;
          specifications?: any;
          datasheet_url?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      suppliers: {
        Row: { id: string; name: string; contact_name: string | null; email: string | null; phone: string | null; website: string | null; country: string | null; lead_time_days: number | null; rating: number | null; status: 'active' | 'inactive' | 'preferred'; created_at: string; updated_at: string; };
        Insert: { id?: string; name: string; contact_name?: string | null; email?: string | null; phone?: string | null; website?: string | null; country?: string | null; lead_time_days?: number | null; rating?: number | null; status?: 'active' | 'inactive' | 'preferred'; created_at?: string; updated_at?: string; };
        Update: { id?: string; name?: string; contact_name?: string | null; email?: string | null; phone?: string | null; website?: string | null; country?: string | null; lead_time_days?: number | null; rating?: number | null; status?: 'active' | 'inactive' | 'preferred'; created_at?: string; updated_at?: string; };
      };
      documents: {
        Row: { id: string; doc_number: string; name: string; doc_type: 'schematic' | 'datasheet' | 'procedure' | 'specification' | 'report' | 'guidelines' | 'drawing' | 'test_report' | 'work_instruction' | 'spreadsheet'; version: string; status: 'draft' | 'in-review' | 'approved' | 'obsolete'; product_id: string | null; author_id: string | null; reviewer_id: string | null; file_url: string | null; file_size_bytes: number | null; file_type: string | null; tags: string[] | null; metadata: any | null; is_deleted: boolean; source_application: string | null; preferred_viewer: 'browser' | 'excel' | 'eda' | 'cad' | 'download' | null; validation_status: 'pending' | 'validated' | 'warning' | 'failed' | null; validation_notes: string | null; sheet_count: number | null; last_validated_at: string | null; created_at: string; updated_at: string; };
        Insert: { id?: string; doc_number: string; name: string; doc_type: 'schematic' | 'datasheet' | 'procedure' | 'specification' | 'report' | 'guidelines' | 'drawing' | 'test_report' | 'work_instruction' | 'spreadsheet'; version?: string; status?: 'draft' | 'in-review' | 'approved' | 'obsolete'; product_id?: string | null; author_id?: string | null; reviewer_id?: string | null; file_url?: string | null; file_size_bytes?: number | null; file_type?: string | null; tags?: string[] | null; metadata?: any | null; is_deleted?: boolean; source_application?: string | null; preferred_viewer?: 'browser' | 'excel' | 'eda' | 'cad' | 'download' | null; validation_status?: 'pending' | 'validated' | 'warning' | 'failed' | null; validation_notes?: string | null; sheet_count?: number | null; last_validated_at?: string | null; created_at?: string; updated_at?: string; };
        Update: { id?: string; doc_number?: string; name?: string; doc_type?: 'schematic' | 'datasheet' | 'procedure' | 'specification' | 'report' | 'guidelines' | 'drawing' | 'test_report' | 'work_instruction' | 'spreadsheet'; version?: string; status?: 'draft' | 'in-review' | 'approved' | 'obsolete'; product_id?: string | null; author_id?: string | null; reviewer_id?: string | null; file_url?: string | null; file_size_bytes?: number | null; file_type?: string | null; tags?: string[] | null; metadata?: any | null; is_deleted?: boolean; source_application?: string | null; preferred_viewer?: 'browser' | 'excel' | 'eda' | 'cad' | 'download' | null; validation_status?: 'pending' | 'validated' | 'warning' | 'failed' | null; validation_notes?: string | null; sheet_count?: number | null; last_validated_at?: string | null; created_at?: string; updated_at?: string; };
      };
      bom_headers: {
        Row: { id: string; bom_number: string; name: string; description: string | null; product_id: string | null; bom_type: 'engineering' | 'manufacturing' | 'service' | 'configurable'; revision: string; status: 'draft' | 'released' | 'obsolete'; total_cost: number | null; created_by: string | null; approved_by: string | null; approved_at: string | null; tags: string[] | null; metadata: any | null; is_deleted: boolean; spreadsheet_status: 'not-linked' | 'linked' | 'imported' | 'exported'; spreadsheet_last_synced_at: string | null; import_profile: string | null; created_at: string; updated_at: string; };
        Insert: { id?: string; bom_number: string; name: string; description?: string | null; product_id?: string | null; bom_type?: 'engineering' | 'manufacturing' | 'service' | 'configurable'; revision?: string; status?: 'draft' | 'released' | 'obsolete'; total_cost?: number | null; created_by?: string | null; approved_by?: string | null; approved_at?: string | null; tags?: string[] | null; metadata?: any | null; is_deleted?: boolean; spreadsheet_status?: 'not-linked' | 'linked' | 'imported' | 'exported'; spreadsheet_last_synced_at?: string | null; import_profile?: string | null; created_at?: string; updated_at?: string; };
        Update: { id?: string; bom_number?: string; name?: string; description?: string | null; product_id?: string | null; bom_type?: 'engineering' | 'manufacturing' | 'service' | 'configurable'; revision?: string; status?: 'draft' | 'released' | 'obsolete'; total_cost?: number | null; created_by?: string | null; approved_by?: string | null; approved_at?: string | null; tags?: string[] | null; metadata?: any | null; is_deleted?: boolean; spreadsheet_status?: 'not-linked' | 'linked' | 'imported' | 'exported'; spreadsheet_last_synced_at?: string | null; import_profile?: string | null; created_at?: string; updated_at?: string; };
      };
      bom_items: {
        Row: { id: string; bom_id: string; component_id: string | null; part_number: string; description: string; quantity: number; uom: string; reference_designators: string[] | null; unit_cost: number | null; status: 'active' | 'pending' | 'obsolete'; sort_order: number; created_at: string; updated_at: string; };
        Insert: { id?: string; bom_id: string; component_id?: string | null; part_number: string; description: string; quantity: number; uom?: string; reference_designators?: string[] | null; unit_cost?: number | null; status?: 'active' | 'pending' | 'obsolete'; sort_order?: number; created_at?: string; updated_at?: string; };
        Update: { id?: string; bom_id?: string; component_id?: string | null; part_number?: string; description?: string; quantity?: number; uom?: string; reference_designators?: string[] | null; unit_cost?: number | null; status?: 'active' | 'pending' | 'obsolete'; sort_order?: number; created_at?: string; updated_at?: string; };
      };
      change_requests: {
        Row: { id: string; cr_number: string; title: string; description: string | null; change_type: 'design' | 'component' | 'process' | 'documentation'; priority: 'low' | 'medium' | 'high' | 'critical'; status: 'submitted' | 'under-review' | 'approved' | 'rejected' | 'implemented'; risk_level: 'low' | 'medium' | 'high'; requestor_id: string | null; assignee_id: string | null; estimated_cost: number | null; target_date: string | null; created_at: string; updated_at: string; };
        Insert: { id?: string; cr_number: string; title: string; description?: string | null; change_type: 'design' | 'component' | 'process' | 'documentation'; priority?: 'low' | 'medium' | 'high' | 'critical'; status?: 'submitted' | 'under-review' | 'approved' | 'rejected' | 'implemented'; risk_level?: 'low' | 'medium' | 'high'; requestor_id?: string | null; assignee_id?: string | null; estimated_cost?: number | null; target_date?: string | null; created_at?: string; updated_at?: string; };
        Update: { id?: string; cr_number?: string; title?: string; description?: string | null; change_type?: 'design' | 'component' | 'process' | 'documentation'; priority?: 'low' | 'medium' | 'high' | 'critical'; status?: 'submitted' | 'under-review' | 'approved' | 'rejected' | 'implemented'; risk_level?: 'low' | 'medium' | 'high'; requestor_id?: string | null; assignee_id?: string | null; estimated_cost?: number | null; target_date?: string | null; created_at?: string; updated_at?: string; };
      };
      change_orders: {
        Row: { id: string; eco_number: string; title: string; description: string | null; change_request_id: string | null; status: 'draft' | 'pending-approval' | 'approved' | 'in-progress' | 'completed' | 'cancelled'; priority: 'low' | 'medium' | 'high' | 'critical'; approver_id: string | null; implementer_id: string | null; estimated_effort_hours: number | null; actual_effort_hours: number | null; cost: number | null; risk_assessment: string | null; testing_required: boolean; target_completion_date: string | null; actual_completion_date: string | null; created_at: string; updated_at: string; };
        Insert: { id?: string; eco_number: string; title: string; description?: string | null; change_request_id?: string | null; status?: 'draft' | 'pending-approval' | 'approved' | 'in-progress' | 'completed' | 'cancelled'; priority?: 'low' | 'medium' | 'high' | 'critical'; approver_id?: string | null; implementer_id?: string | null; estimated_effort_hours?: number | null; actual_effort_hours?: number | null; cost?: number | null; risk_assessment?: string | null; testing_required?: boolean; target_completion_date?: string | null; actual_completion_date?: string | null; created_at?: string; updated_at?: string; };
        Update: { id?: string; eco_number?: string; title?: string; description?: string | null; change_request_id?: string | null; status?: 'draft' | 'pending-approval' | 'approved' | 'in-progress' | 'completed' | 'cancelled'; priority?: 'low' | 'medium' | 'high' | 'critical'; approver_id?: string | null; implementer_id?: string | null; estimated_effort_hours?: number | null; actual_effort_hours?: number | null; cost?: number | null; risk_assessment?: string | null; testing_required?: boolean; target_completion_date?: string | null; actual_completion_date?: string | null; created_at?: string; updated_at?: string; };
      };
      revisions: {
        Row: { id: string; product_id: string; revision_label: string; change_order_id: string | null; description: string | null; revision_type: 'major' | 'minor' | 'patch' | null; impact_level: 'low' | 'medium' | 'high' | null; released_by: string | null; released_at: string | null; is_current: boolean; created_at: string; };
        Insert: { id?: string; product_id: string; revision_label: string; change_order_id?: string | null; description?: string | null; revision_type?: 'major' | 'minor' | 'patch' | null; impact_level?: 'low' | 'medium' | 'high' | null; released_by?: string | null; released_at?: string | null; is_current?: boolean; created_at?: string; };
        Update: { id?: string; product_id?: string; revision_label?: string; change_order_id?: string | null; description?: string | null; revision_type?: 'major' | 'minor' | 'patch' | null; impact_level?: 'low' | 'medium' | 'high' | null; released_by?: string | null; released_at?: string | null; is_current?: boolean; created_at?: string; };
      };
      inventory: {
        Row: { id: string; component_id: string | null; part_number: string; description: string; location: string | null; category: string | null; component_type: 'ic' | 'passive' | 'connector' | 'mechanical' | 'pcb' | null; manufacturer: string | null; quantity_on_hand: number; quantity_allocated: number; reorder_point: number; unit_cost: number | null; package_type: string | null; specifications: any | null; status: 'in-stock' | 'low-stock' | 'out-of-stock' | 'excess'; created_at: string; updated_at: string; };
        Insert: { id?: string; component_id?: string | null; part_number: string; description: string; location?: string | null; category?: string | null; component_type?: 'ic' | 'passive' | 'connector' | 'mechanical' | 'pcb' | null; manufacturer?: string | null; quantity_on_hand?: number; quantity_allocated?: number; reorder_point?: number; unit_cost?: number | null; package_type?: string | null; specifications?: any | null; status?: 'in-stock' | 'low-stock' | 'out-of-stock' | 'excess'; created_at?: string; updated_at?: string; };
        Update: { id?: string; component_id?: string | null; part_number?: string; description?: string; location?: string | null; category?: string | null; component_type?: 'ic' | 'passive' | 'connector' | 'mechanical' | 'pcb' | null; manufacturer?: string | null; quantity_on_hand?: number; quantity_allocated?: number; reorder_point?: number; unit_cost?: number | null; package_type?: string | null; specifications?: any | null; status?: 'in-stock' | 'low-stock' | 'out-of-stock' | 'excess'; created_at?: string; updated_at?: string; };
      };
      purchase_orders: {
        Row: { id: string; po_number: string; supplier_id: string | null; status: 'draft' | 'submitted' | 'approved' | 'shipped' | 'received' | 'cancelled'; order_date: string; delivery_date: string | null; total_amount: number | null; notes: string | null; created_by: string | null; created_at: string; updated_at: string; };
        Insert: { id?: string; po_number: string; supplier_id?: string | null; status?: 'draft' | 'submitted' | 'approved' | 'shipped' | 'received' | 'cancelled'; order_date?: string; delivery_date?: string | null; total_amount?: number | null; notes?: string | null; created_by?: string | null; created_at?: string; updated_at?: string; };
        Update: { id?: string; po_number?: string; supplier_id?: string | null; status?: 'draft' | 'submitted' | 'approved' | 'shipped' | 'received' | 'cancelled'; order_date?: string; delivery_date?: string | null; total_amount?: number | null; notes?: string | null; created_by?: string | null; created_at?: string; updated_at?: string; };
      };
      faq_cache: {
        Row: {
          id: string;
          question_normalized: string;
          question_original: string;
          route_kind: 'ontology-only' | 'operational-only' | 'mixed' | 'external-only';
          answer: string;
          tool_names: string[] | null;
          hit_count: number;
          created_by: string | null;
          last_used_at: string;
          expires_at: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          question_normalized: string;
          question_original: string;
          route_kind: 'ontology-only' | 'operational-only' | 'mixed' | 'external-only';
          answer: string;
          tool_names?: string[] | null;
          hit_count?: number;
          created_by?: string | null;
          last_used_at?: string;
          expires_at: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          question_normalized?: string;
          question_original?: string;
          route_kind?: 'ontology-only' | 'operational-only' | 'mixed' | 'external-only';
          answer?: string;
          tool_names?: string[] | null;
          hit_count?: number;
          created_by?: string | null;
          last_used_at?: string;
          expires_at?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      activity_log: {
        Row: { id: string; user_id: string | null; entity_type: string; entity_id: string; action: string; metadata: any; created_at: string; };
        Insert: { id?: string; user_id?: string | null; entity_type: string; entity_id: string; action: string; metadata?: any; created_at?: string; };
        Update: { id?: string; user_id?: string | null; entity_type?: string; entity_id?: string; action?: string; metadata?: any; created_at?: string; };
      };
    };
  };
}

export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
export type InsertTables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Insert'];
export type UpdateTables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Update'];